/**
 * Offline lo-fi colouring pipeline — Phase 3 of the Slicer
 * overhaul. Three independent stages the user can stack:
 *
 *   1. **Bit-crush** — quantize PCM to fewer bits-per-sample.
 *      4-bit / 8-bit / 12-bit targets cover the S950 (12),
 *      SP-1200 (8) and Amiga (8) flavours. Implementation is the
 *      same mid-tread quantizer as the Bitcrush FX that already
 *      ships in the master bus, just applied destructively to the
 *      source buffer instead of on the mix path.
 *
 *   2. **Sample-rate downsample** — decimate to a lower SR with
 *      a rudimentary anti-alias prefilter, then upsample back to
 *      the host rate so the rest of the engine doesn't have to
 *      know. Leaves the aliasing artifacts from the gap between
 *      the filter's passband and Nyquist — that's the sound of
 *      the 26–32 kHz hardware samplers the user is chasing.
 *
 *   3. **Cyclic stretch** — S950-style. Chop the source into
 *      short fixed cycles and either repeat (stretch > 1) or
 *      drop (stretch < 1) cycles with a small sinc crossfade at
 *      the boundaries. Not a full WSOLA (no similarity search)
 *      because the ringing it produces *is* the aesthetic the
 *      user is reaching for — close-but-not-perfect phase
 *      continuity is what gives jungle breaks their bite.
 *
 * All three stages work on Float32 PCM in the [-1, 1] range and
 * return new AudioBuffer instances, leaving the source untouched
 * so the Slicer can reset to pristine at any time.
 */

/** Supported bit-depths for the crusher. */
export type BitDepth = 4 | 8 | 12 | 16

/** Preset one-tap loadouts. UI surfaces these as buttons. */
export const LOFI_PRESETS = {
  /** Akai S950 — 12-bit / 32 kHz / cyclic stretch */
  s950: {
    bitDepth: 12 as BitDepth,
    sampleRateHz: 32000,
    stretchMode: 'wsola' as const,
    cycleMs: 50,
  },
  /** Akai SP-1200 — 8-bit / 26 kHz / cyclic stretch */
  sp1200: {
    bitDepth: 8 as BitDepth,
    sampleRateHz: 26000,
    stretchMode: 'wsola' as const,
    cycleMs: 80,
  },
  /** Commodore Amiga Paula — 8-bit / 22 kHz / clean stretch */
  amiga: {
    bitDepth: 8 as BitDepth,
    sampleRateHz: 22050,
    stretchMode: 'clean' as const,
    cycleMs: 50,
  },
} as const

export interface LoFiParams {
  /** Output bit-depth. 16 = no crush (identity). */
  bitDepth: BitDepth
  /** Target sample rate in Hz. `null` = no downsampling. */
  sampleRateHz: number | null
  /** Stretch algorithm for the source warp. `clean` defers to
   *  the normal Rubber Band pipeline (this module doesn't
   *  re-apply it — the caller does). `wsola` runs the cyclic
   *  stretch inside this module. */
  stretchMode: 'clean' | 'wsola'
  /** Cycle length in milliseconds for the cyclic stretch.
   *  Shorter cycles = more artifact density; 50 ms is the
   *  S950's approximate setting. */
  cycleMs: number
}

/**
 * Bit-crush a buffer to the given depth. 16-bit is a no-op
 * (and is returned as the input reference so the cache stays
 * O(1) in that path). For everything lower, we compute
 * `2^(bits-1) - 1` discrete steps between −1 and +1 and round
 * each sample to the nearest level. Produces the characteristic
 * stair-step quantization noise on any content below full scale.
 */
export function bitCrush(buffer: AudioBuffer, bitDepth: BitDepth): AudioBuffer {
  if (bitDepth >= 16) return buffer
  const levels = Math.pow(2, bitDepth - 1) - 1
  const out = new OfflineAudioContext(
    Math.max(buffer.numberOfChannels, 1),
    buffer.length,
    buffer.sampleRate,
  ).createBuffer(buffer.numberOfChannels, buffer.length, buffer.sampleRate)
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const src = buffer.getChannelData(c)
    const dst = out.getChannelData(c)
    for (let i = 0; i < src.length; i++) {
      const v = Math.max(-1, Math.min(1, src[i]))
      dst[i] = Math.round(v * levels) / levels
    }
  }
  return out
}

/**
 * Decimate to `targetRate` and resample back up to the source
 * rate. The round-trip leaves us at the same buffer length so
 * downstream code (slice offsets etc.) doesn't need to know
 * about the transform. Uses a cheap single-pole lowpass at
 * `targetRate / 2` as the anti-alias / reconstruction filter.
 * Yes, better filters exist; the rough edges are the point.
 */
export function sampleRateCrunch(
  buffer: AudioBuffer,
  targetRate: number,
): AudioBuffer {
  if (
    !Number.isFinite(targetRate) ||
    targetRate <= 0 ||
    targetRate >= buffer.sampleRate
  ) {
    return buffer
  }
  const hostRate = buffer.sampleRate
  const out = new OfflineAudioContext(
    Math.max(buffer.numberOfChannels, 1),
    buffer.length,
    hostRate,
  ).createBuffer(buffer.numberOfChannels, buffer.length, hostRate)

  // Single-pole IIR at f_c = targetRate / 2, run forward-only
  // (we don't care about phase here — the artifacts are welcome).
  const cutoff = targetRate / 2
  const rc = 1 / (2 * Math.PI * cutoff)
  const dt = 1 / hostRate
  const alpha = dt / (rc + dt)

  const ratio = targetRate / hostRate

  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const src = buffer.getChannelData(c)
    const dst = out.getChannelData(c)
    // Stage 1 — lowpass + sample-and-hold at the target rate.
    // Each host-rate sample reads either a freshly-grabbed
    // target-rate sample or the held value from the previous
    // decimation boundary. That's where the stepping comes from.
    let y = 0
    let lastHeld = 0
    let phase = 0
    for (let i = 0; i < src.length; i++) {
      y = y + alpha * (src[i] - y)
      phase += ratio
      if (phase >= 1) {
        phase -= 1
        lastHeld = y
      }
      dst[i] = lastHeld
    }
  }
  return out
}

/**
 * S950-style cyclic stretch. Chop the source into fixed-length
 * cycles (`cycleMs`) and either repeat or drop cycles to hit the
 * target length. A small half-Hann crossfade at each boundary
 * masks the discontinuity without trying to phase-match — the
 * residual ringing is the aesthetic.
 *
 *  - `timeRatio > 1` → stretch (output longer). Cycles are
 *    repeated in a round-robin pattern so repeats spread evenly.
 *  - `timeRatio < 1` → compress (output shorter). Cycles are
 *    dropped in a mirror-image pattern.
 *  - `timeRatio === 1` → identity, returns the source ref.
 */
export function cyclicStretch(
  buffer: AudioBuffer,
  timeRatio: number,
  cycleMs = 50,
): AudioBuffer {
  if (!Number.isFinite(timeRatio) || Math.abs(timeRatio - 1) < 1e-4) {
    return buffer
  }
  const ratio = Math.max(0.25, Math.min(4, timeRatio))
  const sampleRate = buffer.sampleRate
  const cycleLen = Math.max(32, Math.round((cycleMs / 1000) * sampleRate))
  const fadeLen = Math.min(Math.floor(cycleLen / 8), 128)
  const cycleCount = Math.max(1, Math.ceil(buffer.length / cycleLen))
  const outCycleCount = Math.max(1, Math.round(cycleCount * ratio))
  const outLength = outCycleCount * cycleLen

  const out = new OfflineAudioContext(
    Math.max(buffer.numberOfChannels, 1),
    outLength,
    sampleRate,
  ).createBuffer(buffer.numberOfChannels, outLength, sampleRate)

  // Build the cycle-index schedule once and apply to every
  // channel so L + R stay time-aligned.
  const schedule = new Int32Array(outCycleCount)
  for (let i = 0; i < outCycleCount; i++) {
    // Map output cycle i → input cycle index, rounding to the
    // nearest source cycle. For ratios > 1 this produces
    // repeats; for < 1 it skips.
    schedule[i] = Math.min(
      cycleCount - 1,
      Math.max(0, Math.round((i / outCycleCount) * cycleCount)),
    )
  }

  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const src = buffer.getChannelData(c)
    const dst = out.getChannelData(c)
    for (let o = 0; o < outCycleCount; o++) {
      const srcStart = schedule[o] * cycleLen
      const dstStart = o * cycleLen
      const take = Math.min(cycleLen, src.length - srcStart)
      for (let i = 0; i < take; i++) {
        dst[dstStart + i] = src[srcStart + i]
      }
      // Half-Hann crossfade at the cycle head — taper the first
      // `fadeLen` samples into the previous cycle's tail. Avoids
      // the hard "click" at the repeat boundary without trying
      // to align phase.
      if (o > 0 && fadeLen > 0) {
        for (let i = 0; i < fadeLen; i++) {
          const w = 0.5 - 0.5 * Math.cos((Math.PI * i) / fadeLen)
          dst[dstStart + i] *= w
        }
      }
    }
  }
  return out
}

/** Apply the whole lo-fi chain to a buffer in one call. Order
 *  matters: stretch first (so the cycle artifacts come through
 *  untouched by downstream stages), then sample-rate crunch,
 *  then bit-crush — same order an S950 would apply them. */
export function applyLoFi(
  buffer: AudioBuffer,
  params: LoFiParams,
  options: { sourceTimeRatio?: number } = {},
): AudioBuffer {
  let out = buffer
  if (
    params.stretchMode === 'wsola' &&
    options.sourceTimeRatio &&
    Math.abs(options.sourceTimeRatio - 1) > 1e-4
  ) {
    out = cyclicStretch(out, options.sourceTimeRatio, params.cycleMs)
  }
  if (params.sampleRateHz !== null) {
    out = sampleRateCrunch(out, params.sampleRateHz)
  }
  if (params.bitDepth < 16) {
    out = bitCrush(out, params.bitDepth)
  }
  return out
}
